import { isDefined } from 'ts-utils';
import { ActionType } from '../../store/ActionTypes';
import { store, StoreStateInternal, useAppDispatch } from '../../store/Store';
import { getScreenAnswers } from '../../util/ConditionalNextScreenSelectors';

export type FlowRoute<ScreenNames extends string> = {
  screenName: ScreenNames;
  /** For conditional navigation, specify a record of keys to screen names. Use a `fallback` key for the default navigation. */
  nextScreen?: ScreenNames | (Record<string, ScreenNames> & { fallback: ScreenNames });
  /** Should return a key defined in `nextScreen`. Defaults to the answers given on the current
   * screen (getScreenAnswer). When multiple items are returned, the first matching key found in
   * `nextScreen` is used. */
  nextScreenSelector?: (
    state: StoreStateInternal,
    currentScreenName: string
  ) => string | string[] | undefined;
};

type FlowNavigator<ScreenNames extends string> = {
  next: (props: FlowRoute<ScreenNames>, resetHistory?: boolean) => void;
  goBack: () => void;
  goTo: (screen: ScreenNames | undefined, resetHistory?: boolean) => void;
};

export type NavigationProps<Props, ScreenNames extends string> = {
  navigation: FlowNavigator<ScreenNames>;
  onShowToast?: () => void;
} & Props;

export function resolveNextScreen<ScreenNames extends string>(
  props: FlowRoute<ScreenNames>
): ScreenNames | undefined {
  const { nextScreen, screenName, nextScreenSelector = getScreenAnswers } = props;
  if (nextScreen === undefined) {
    return undefined;
  }
  if (typeof nextScreen === 'string') {
    return nextScreen;
  }
  // Else conditional navigation
  // If multiple options are selected, prioritise routing to any with explicit conditional routes
  const nextScreenKeys = toArray(nextScreenSelector(store.getState(), screenName));
  const nextScreenKey =
    nextScreenKeys.find((key) => isDefined(key) && isDefined(nextScreen[key])) ?? 'fallback';
  const nextScreenName = nextScreen[nextScreenKey];

  if (nextScreenName === undefined) {
    console.error(
      `FlowNavigator: No ${screenName} screen route or fallback defined for key '${nextScreenKey}'. ` +
        `Keys include: '${Object.keys(nextScreen).join("', '")}'.`
    );
  }

  return nextScreenName;
}

export function useFlowNavigator<ScreenNames extends string>(): FlowNavigator<ScreenNames> {
  const dispatch = useAppDispatch();

  function next<ScreenNames extends string>(props: FlowRoute<ScreenNames>, resetHistory = false) {
    const screen = resolveNextScreen(props);
    navigateToScreen(screen, resetHistory);
  }

  function goBack() {
    dispatch({ type: ActionType.DECREMENT_PAGE });
  }

  function navigateToScreen<ScreenNames extends string>(
    screen: ScreenNames | undefined,
    resetHistory = false
  ) {
    if (screen === undefined) {
      console.error(`FlowNavigator: Attempting to navigate to undefined screen: ${screen}.`);
    } else {
      dispatch({ type: ActionType.SET_PAGE, page: screen, resetHistory });
    }
  }

  return { next, goBack, goTo: navigateToScreen };
}

function toArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}
